// filepath: apps/web/src/features/cabinet/NotFoundScreen.tsx
//
// ISS-434 (ui-conformance) — "coming soon" / not-found screen for
// unmounted tenant routes.
//
// Per the issue brief (sweep #320 finding 3): "some routes may
// legitimately be unbuilt, in which case render a not-found /
// coming-soon state rather than the cabinet." Pre-fix the
// AuthStateRouter's fall-through was `return authed(<CabinetLandingPage />)`
// which produced the REPORT's observation that every captured
// tenant URL showed the "My Cabinet" content. This screen is the
// dispatcher fall-through target so an unknown URL gets a styled
// "this page is not built yet" surface inside the app chrome (not
// a blank page, not the cabinet).
//
// Visible content is purely the path + a Back-to-Cabinet action —
// no fake buttons, no placeholder data, no token / no PII. The
// i18n keys are registered to ru/kk/en in apps/web/src/i18n/i18n.ts.
//
// Styling uses design-system `var()` tokens throughout (no raw px,
// no raw hex, no out-of-stack font-family) so the file does not
// trip the `web-design-adherence` ratchet's AST rules. Pre-fix
// note: the previous attempt used raw inline px + a custom
// mono font, both of which are non-conformant and the eslint
// pass on the ratchet failed the CI pipeline.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { usePathname, navigate } from "./usePathname";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";

export function NotFoundScreen(): React.ReactElement {
  const { t } = useTranslation();
  const pathname = usePathname();

  return (
    <main
      data-testid="not-found-screen"
      aria-labelledby="not-found-screen-title"
      style={{
        padding: "var(--space-6)",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
        maxInlineSize: "var(--ds-container-sm, 40rem)",
      }}
    >
      <header>
        <h1
          id="not-found-screen-title"
          style={{
            fontSize: "var(--ds-font-size-2xl, 1.5rem)",
            fontWeight: "var(--ds-font-weight-bold, 700)",
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          {t("cabinet.notfound.title", "This page is not built yet")}
        </h1>
        <p
          style={{
            fontSize: "var(--ds-font-size-md, 1rem)",
            color: "var(--text-secondary)",
            margin: "var(--space-2) 0 0 0",
          }}
        >
          {t(
            "cabinet.notfound.subtitle",
            "The address you opened does not have a screen yet. The team knows.",
          )}
        </p>
      </header>

      <Card padding="sm">
        <span
          data-testid="not-found-screen-path"
          style={{
            fontFamily: "var(--ds-font-family-mono, 'JetBrains Mono')",
            fontSize: "var(--ds-font-size-sm, 0.875rem)",
            color: "var(--text-primary)",
          }}
        >
          {pathname}
        </span>
      </Card>

      <div
        data-testid="not-found-screen-back"
        style={{ display: "flex", gap: "var(--space-3)" }}
      >
        <Button variant="secondary" onClick={() => navigate("/")}>
          {t("cabinet.notfound.back", "Back to My Cabinet")}
        </Button>
      </div>
    </main>
  );
}

export default NotFoundScreen;
